import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLines, SectionHead } from "@/components/Reveal";

const CASES = [
  {
    n: "01",
    tag: "Trenching",
    title: "Utility trenches, dug once",
    body: "Depth-locked guidance holds the bucket inside a ±30 mm green zone for the whole run — no second pass, no bedding waste, nobody down the hole with a laser.",
    img: "https://images.pexels.com/photos/29274530/pexels-photo-29274530.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Pipe installation in an urban trench",
    rows: [
      ["System", <Link to="/products/2.5d" data-testid="work-case-emg100-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">EasyNAV EMG100</Link>],
      ["Machine", "[your make / model]"],
      ["Scope", "[0,000] m of trench"],
      ["Result", "[your result — e.g. zero over-cut]"],
    ],
  },
  {
    n: "02",
    tag: "Slopes",
    title: "Slopes cut to the drawing",
    body: "The design model rides in the cab. Cut the batter once, on grade, with live cross-slope readout — no string lines pegged down the face.",
    img: "https://images.unsplash.com/photo-1765972280045-7d0e47b376e3?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Bulldozer working a slope on a construction site",
    rows: [
      ["System", <Link to="/products/3d" data-testid="work-case-tx73-slope-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">MCNAV TX73</Link>],
      ["Machine", "[your make / model]"],
      ["Scope", "[0,000] m² at 1:[n] batter"],
      ["Result", "[your result — e.g. one-pass batters]"],
    ],
  },
  {
    n: "03",
    tag: "Foundations",
    title: "Foundations on the first pass",
    body: "Pad outlines and levels straight from the model. Dig to design, verify as-built on screen, and hand the engineer a clean hole.",
    img: "https://images.pexels.com/photos/36657008/pexels-photo-36657008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Excavator moving earth on a foundation site",
    rows: [
      ["System", <Link to="/products/3d" data-testid="work-case-tx73-pads-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">MCNAV TX73</Link>],
      ["Machine", "[your make / model]"],
      ["Scope", "[n] pads to [depth] m"],
      ["Result", "[your result — e.g. zero rework]"],
    ],
  },
];

function JobRow({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-line py-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog">{k}</span>
      <span className="text-right font-mono text-xs text-slate-300 sm:text-sm">{v}</span>
    </div>
  );
}

export default function Work() {
  return (
    <main>
      <section className="relative overflow-hidden border-b border-line px-4 pb-16 pt-32 sm:px-8 lg:px-16 lg:pb-24 lg:pt-40" data-testid="work-hero">
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto max-w-[1600px]">
          <FadeUp y={12}>
            <Eyebrow>Field proof</Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.12}
            lines={["Trenches. Slopes.", { text: "Foundations.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-8xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            The jobs machine control was built for — dug on grade with a{" "}
            <Link to="/precision-solutions" data-testid="work-chcnav-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
              CHCNAV
            </Link>{" "}
            system. The result slots below are
            open: our first Green Grade job photos and numbers land here as crews finish, and yours could be first.
          </p>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="work-cases">
        <div className="mx-auto max-w-[1600px] space-y-24 lg:space-y-32">
          {CASES.map((c, i) => (
            <div
              key={c.n}
              className={`grid items-center gap-10 lg:grid-cols-12 lg:gap-16 ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
            >
              <div className="lg:col-span-2 lg:[direction:ltr]">
                <FadeUp>
                  <span className="text-stroke font-display text-7xl font-black leading-none sm:text-8xl">{c.n}</span>
                </FadeUp>
              </div>
              <div className="lg:col-span-4 lg:[direction:ltr]">
                <FadeUp delay={0.08}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">{c.tag}</p>
                  <h2 className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
                    {c.title}
                  </h2>
                  <p className="mt-5 text-base leading-relaxed text-fog">{c.body}</p>
                  <div className="mt-6" data-testid={`work-case-rows-${i}`}>
                    {c.rows.map(([k, v]) => (
                      <JobRow key={k} k={k} v={v} />
                    ))}
                  </div>
                </FadeUp>
              </div>
              <div className="lg:col-span-6 lg:[direction:ltr]">
                <FadeUp delay={0.16}>
                  <div className="group relative overflow-hidden border border-line" data-testid={`work-case-${i}`}>
                    <img
                      src={c.img}
                      alt={c.alt}
                      loading="lazy"
                      className="clip-frame h-72 w-full object-cover grayscale-[30%] transition-transform duration-700 group-hover:scale-105 sm:h-96"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                    <span className="absolute left-5 top-5 border border-signal/50 bg-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-neon backdrop-blur-sm">
                      Dug on grade
                    </span>
                  </div>
                </FadeUp>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-line" data-testid="work-cta">
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
          <SectionHead
            kicker="Your job here"
            title="First hole's on us."
            body="Book a demo, let the system prove itself on your ground, and your job could be the first real numbers on this page."
          />
          <FadeUp delay={0.15}>
            <Link
              to="/contact"
              data-testid="work-cta-btn"
              className="group mt-10 inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-white"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
